/**
 * chill-recently-viewed
 *
 * Reads product handles from localStorage (key: 'chill_rv'), fetches each
 * product via the Shopify AJAX API, and renders product cards into the
 * .chill-rv__grid element.
 *
 * Tracking (writing to localStorage) happens via a synchronous inline
 * <script> inside sections/chill-recently-viewed.liquid so that the current
 * product is recorded even before this deferred module loads.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'chill_rv';

  /* ── helpers ──────────────────────────────────────────── */

  function getHandles() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
  }

  /**
   * Resize a Shopify CDN image URL by injecting _{size}x before the extension.
   * Works on URLs with or without a ?v= query string.
   */
  function resizedSrc(url, size) {
    if (!url) return '';
    return url.replace(/(\.(jpe?g|png|gif|webp))(\?.*)?$/i, `_${size}x$1$3`);
  }

  /**
   * Format a price integer (cents/kuruş) using the shop's money format.
   * Falls back to Shopify.formatMoney if available, otherwise plain string.
   */
  function formatMoney(cents, format) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format);
    }
    const amount = (cents / 100).toLocaleString('tr-TR', { minimumFractionDigits: 2 });
    if (!format) return amount;
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_with_comma_separator}}', amount)
      .replace('{{amount_no_decimals}}', Math.floor(cents / 100));
  }

  /* ── card renderer ────────────────────────────────────── */

  function renderCard(product, moneyFormat) {
    const url      = '/products/' + product.handle;
    const rawImg   = product.images && product.images.length ? product.images[0] : null;
    const imgSrc   = resizedSrc(rawImg, 600);
    const img2x    = resizedSrc(rawImg, 1200);
    const price    = product.price_min;
    const compare  = product.compare_at_price_min;
    const onSale   = compare && compare > price;
    const safeTitle = product.title.replace(/</g, '&lt;');

    return `
<a href="${url}" class="chill-rv__card" role="listitem" aria-label="${safeTitle}">
  <div class="chill-rv__img">
    ${imgSrc
      ? `<img
           src="${imgSrc}"
           srcset="${imgSrc} 600w, ${img2x} 1200w"
           sizes="(max-width: 749px) 45vw, 22vw"
           alt="${safeTitle}"
           loading="lazy"
           width="600"
           height="800"
         >`
      : '<div class="chill-rv__img-placeholder" aria-hidden="true"></div>'
    }
  </div>
  <div class="chill-rv__meta">
    <p class="chill-rv__name">${safeTitle}</p>
    <p class="chill-rv__price${onSale ? ' chill-rv__price--sale' : ''}">
      ${onSale ? `<s class="chill-rv__compare">${formatMoney(compare, moneyFormat)}</s> ` : ''}${formatMoney(price, moneyFormat)}
    </p>
  </div>
</a>`;
  }

  /* ── section init ─────────────────────────────────────── */

  function initSection(section) {
    const max         = parseInt(section.dataset.max   || '6',  10);
    const current     = section.dataset.current        || '';
    const moneyFormat = section.dataset.moneyFormat    || '';

    const handles = getHandles()
      .filter(h => h !== current)
      .slice(0, max);

    if (!handles.length) return; // nothing to show — keep section hidden

    const grid = section.querySelector('.chill-rv__grid');
    if (!grid) return;

    // Fetch all product data in parallel (low priority — below-the-fold)
    Promise.all(
      handles.map(h =>
        fetch('/products/' + h + '.js', { priority: 'low' })
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      )
    ).then(products => {
      const valid = products.filter(Boolean);
      if (!valid.length) return; // stay hidden

      grid.innerHTML = valid.map(p => renderCard(p, moneyFormat)).join('');
      section.removeAttribute('hidden');
    });
  }

  /* ── boot ─────────────────────────────────────────────── */

  document
    .querySelectorAll('[data-chill-recently-viewed]')
    .forEach(initSection);
})();
